import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getSplitConfiguration, saveSplitConfiguration, editSplitConfiguration, getSplitConfigurationSanbox, saveSplitConfigurationSandbox, editSplitConfigurationSandbox } from '../../services/transaction';
import { useAuth } from "../../context/AuthContextProvider";

// Mock delete for now, if endpoint is provided, wrap it here
const deleteSplitConfiguration = async (id) => {
    // Example: return await http().delete(`/split/configuration/${id}`);
    return new Promise(resolve => setTimeout(resolve, 500));
};

const SplitConfig = () => {
    // --- Constants ---
    const PALETTE = ['#f58220', '#0ca678', '#f76707', '#7048e8', '#bb4430', '#099268', '#e67700', '#3b5bdb', '#c2255c', '#2f9e44'];
    const MERCHANT_COLOR = '#94a3b8';
    const { globalProfile } = useAuth();

    // --- State ---
    const [activeTab, setActiveTab] = useState('list'); // 'list' or 'form'
    const [configsList, setConfigsList] = useState([]);
    const [editingConfigId, setEditingConfigId] = useState(null);

    const [splitType, setSplitType] = useState('simple');
    const [simpleMode, setSimpleMode] = useState('percent');
    const [simpleSubs, setSimpleSubs] = useState([{ id: 1, name: '', value: '' }]);
    const [complexRules, setComplexRules] = useState([]);
    const [saveStatus, setSaveStatus] = useState('Save Configuration');
    const [isLoading, setIsLoading] = useState(true);

    const nidRef = useRef(100);
    const getNextId = () => nidRef.current++;

    const getSplitConfigFunc = globalProfile === 'TEST' ? getSplitConfigurationSanbox : getSplitConfiguration;

    const saveSplitConfigReqFunc = globalProfile === 'TEST' ? saveSplitConfigurationSandbox : saveSplitConfiguration;

    const editSplitConfigReqFunc = globalProfile === 'TEST' ? editSplitConfigurationSandbox : editSplitConfiguration;


    // --- Initial Load ---
    const fetchConfig = async () => {
        setIsLoading(true);
        try {
            const res = await getSplitConfigFunc();
            if (res?.configurations && res.configurations.length > 0) {
                setConfigsList(res.configurations);
            } else {
                setConfigsList([]);
            }
        } catch (error) {
            console.error("Failed to load configs", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Logic ---
    const addSimpleSub = () => setSimpleSubs([...simpleSubs, { id: getNextId(), name: '', value: '' }]);
    const removeSimpleSub = (id) => setSimpleSubs(simpleSubs.filter(s => s.id !== id));
    const updateSimpleSub = (id, field, val) => setSimpleSubs(simpleSubs.map(s => s.id === id ? { ...s, [field]: val } : s));

    const simpleBeneTotal = useMemo(() => simpleSubs.reduce((s, x) => s + (parseFloat(x.value) || 0), 0), [simpleSubs]);
    const simpleIsOver = simpleMode === 'percent' && simpleBeneTotal > 100;
    const simpleRemainder = simpleMode === 'percent' ? Math.max(0, 100 - simpleBeneTotal) : null;

    const addComplexRule = () => setComplexRules([...complexRules, { id: getNextId(), keyword: '', mode: 'percent', subs: [{ id: getNextId(), name: '', value: '' }] }]);
    const removeComplexRule = (id) => setComplexRules(complexRules.filter(r => r.id !== id));
    const updateComplexRuleField = (ruleId, field, val) => setComplexRules(complexRules.map(r => r.id === ruleId ? { ...r, [field]: val } : r));
    const addComplexRuleSub = (ruleId) => setComplexRules(complexRules.map(r => r.id === ruleId ? { ...r, subs: [...r.subs, { id: getNextId(), name: '', value: '' }] } : r));
    const removeComplexRuleSub = (ruleId, subId) => setComplexRules(complexRules.map(r => r.id === ruleId ? { ...r, subs: r.subs.filter(s => s.id !== subId) } : r));
    const updateComplexRuleSub = (ruleId, subId, field, val) => setComplexRules(complexRules.map(r => r.id === ruleId ? { ...r, subs: r.subs.map(s => s.id === subId ? { ...s, [field]: val } : s) } : r));

    // --- Actions ---
    const handleEdit = (config) => {
        setEditingConfigId(config.id);
        const isComplex = config.splitType.toLowerCase() === 'complex';
        setSplitType(isComplex ? 'complex' : 'simple');

        if (isComplex) {
            setComplexRules((config.rules || []).map(r => {
                const firstSubMode = r.beneficiaries?.[0]?.mode?.toLowerCase() === 'flat' ? 'flat' : 'percent';
                return {
                    id: getNextId(),
                    keyword: r.keyword || '',
                    mode: firstSubMode,
                    subs: (r.beneficiaries || []).map(s => ({
                        id: getNextId(),
                        name: s.name || '',
                        value: s.value !== undefined ? String(s.value) : ''
                    }))
                };
            }));
        } else {
            const firstSubMode = config.beneficiaries?.[0]?.mode?.toLowerCase() === 'flat' ? 'flat' : 'percent';
            setSimpleMode(firstSubMode);
            setSimpleSubs((config.beneficiaries || []).map(s => ({
                id: getNextId(),
                name: s.name || '',
                value: s.value !== undefined ? String(s.value) : ''
            })));
        }

        setActiveTab('form');
    };

    const handleRemove = async (config) => {
        if (!window.confirm("Are you sure you want to remove this configuration?")) return;
        setIsLoading(true);
        try {
            await deleteSplitConfiguration(config.id || config.splitType);
            await fetchConfig();
        } catch (error) {
            console.error("Failed to delete", error);
            alert("Failed to delete configuration.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateNew = () => {
        setEditingConfigId(null);
        setSplitType('simple');
        setSimpleSubs([{ id: getNextId(), name: '', value: '' }]);
        setComplexRules([{ id: getNextId(), keyword: '', mode: 'percent', subs: [{ id: getNextId(), name: '', value: '' }] }]);
        setActiveTab('form');
    };

    const saveConfig = async () => {
        if (splitType === 'simple' && simpleIsOver) return;

        let payload = {
            splitType: splitType.charAt(0).toUpperCase() + splitType.slice(1),
            beneficiaries: [],
            rules: []
        };

        if (splitType === 'simple') {
            payload.beneficiaries = simpleSubs.filter(s => s.name && s.value !== '').map(s => ({
                name: s.name,
                mode: simpleMode === 'percent' ? 'Percentage' : 'Flat',
                value: parseFloat(s.value) || 0
            }));
        } else {
            try {
                payload.rules = complexRules.map(rule => {
                    const total = rule.subs.reduce((s, x) => s + (parseFloat(x.value) || 0), 0);
                    if (rule.mode === 'percent' && total > 100) throw new Error(`Rule "${rule.keyword}" exceeds 100%`);

                    return {
                        keyword: rule.keyword,
                        beneficiaries: rule.subs.filter(s => s.name && s.value !== '').map(s => ({
                            name: s.name,
                            mode: rule.mode === 'percent' ? 'Percentage' : 'Flat',
                            value: parseFloat(s.value) || 0
                        }))
                    };
                });
            } catch (e) {
                alert(e.message);
                return;
            }
        }

        setSaveStatus('Saving...');
        try {
            if (editingConfigId) {
                await editSplitConfigReqFunc(editingConfigId, payload);
            } else {
                await saveSplitConfigReqFunc(payload);
            }
            setSaveStatus('✓ Saved!');
            await fetchConfig();
            setTimeout(() => {
                setSaveStatus('Save Configuration');
                setActiveTab('list');
            }, 1000);
        } catch (error) {
            console.error("Failed to save", error);
            setSaveStatus('Failed to save');
            setTimeout(() => setSaveStatus('Save Configuration'), 2000);
        }
    };

    // --- Components ---
    const AllocationBar = ({ subsArr, beneTotal, remainder, isOver, mode }) => {
        if (mode !== 'percent') return null;
        const segs = subsArr.filter(s => parseFloat(s.value) > 0).map((s, i) => ({
            name: s.name || `Bene ${i + 1}`, value: parseFloat(s.value) || 0, color: PALETTE[i % PALETTE.length]
        }));
        if (!isOver && remainder > 0) segs.push({ name: 'Merchant', value: remainder, color: MERCHANT_COLOR });
        const dispTotal = isOver ? beneTotal : 100;
        return (
            <div className="mt-5">
                <div className="flex justify-between text-[0.7rem] font-semibold text-[#8888aa] mb-2">
                    <span>Allocation Breakdown</span>
                    <span style={{ color: isOver ? '#bb4430' : '#8888aa' }}>{isOver ? `${beneTotal.toFixed(1)}% assigned — exceeds 100%` : `${beneTotal.toFixed(1)}% to beneficiaries · ${remainder.toFixed(1)}% to you`}</span>
                </div>
                <div className="h-[10px] rounded-full bg-[#f0efe9] border border-[#e2e0d8] overflow-hidden flex">
                    {segs.map((seg, i) => (
                        <div key={i} style={{ width: `${(seg.value / dispTotal * 100)}%`, background: seg.color }} className="h-full transition-all duration-300"></div>
                    ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    {segs.map((seg, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-[0.7rem] text-[#4a4a6a]">
                            <div className="w-2 h-2 rounded-full" style={{ background: seg.color }}></div>
                            <span>{seg.name}</span>
                            <span className="font-bold text-[#030229]">{seg.value.toFixed(1)}%</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-[860px] mx-auto p-10 bg-[#fafafa] font-['Lato'] text-[#030229] min-h-screen">
            <style>{`
                .card { background: #fff; border: 1px solid #e2e0d8; border-radius: 14px; padding: 24px; margin-bottom: 16px; box-shadow: 0 1px 4px rgba(3,2,41,0.07); }
                input, select { width: 100%; border: 1.5px solid #e2e0d8; font-size: 0.85rem; padding: 10px 13px; border-radius: 9px; outline: none; transition: border-color 0.18s; }
                input:focus, select:focus { border-color: #f58220; box-shadow: 0 0 0 3px rgba(245,130,32,0.08); }
                .merchant-row { display: grid; grid-template-columns: 1fr 130px 32px; gap: 8px; align-items: center; background: #fff4e6; border: 1.5px solid rgba(245,130,32,0.18); border-radius: 9px; padding: 10px 12px; }
                .btn-primary { background: #f58220; color: #fff; font-size: 0.85rem; font-weight: 700; padding: 11px 26px; border-radius: 9px; cursor: pointer; border: none; box-shadow: 0 2px 8px rgba(245,130,32,0.25); transition: background-color 0.2s;}
                .btn-primary:active { transform: scale(0.98); }
                .btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
                .type-btn { padding: 10px 26px; border: none; background: transparent; color: #8888aa; font-size: 0.85rem; font-weight: 600; cursor: pointer; border-radius: 8px; }
                .type-btn.active { background: #f58220; color: #fff; }
                .loading-overlay { position: fixed; inset: 0; background: rgba(255,255,255,0.7); display: flex; align-items: center; justify-content: center; z-index: 50; }
                
                /* List tab styles */
                .config-list-item { background: #fff; border: 1px solid #e2e0d8; border-radius: 12px; padding: 20px; margin-bottom: 16px; transition: all 0.2s; display: flex; justify-content: space-between; align-items: center; }
                .split-badge { display: inline-block; padding: 4px 10px; border-radius: 6px; font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
                .badge-simple { background: #ebfbee; color: #0ca678; }
                .badge-complex { background: #f3f0ff; color: #7048e8; }
                .action-btn { background: #fff; border: 1px solid #e2e0d8; border-radius: 6px; padding: 6px 14px; font-size: 0.8rem; font-weight: 600; color: #4a4a6a; cursor: pointer; transition: all 0.15s; }
                .action-btn:hover { border-color: #f58220; color: #f58220; }
                .action-btn-danger { color: #e03131; }
                .action-btn-danger:hover { border-color: #e03131; background: #fff5f5; }
                .inner-nav { display: flex; border-bottom: 1.5px solid #e2e0d8; margin-bottom: 24px; gap: 24px; }
                .inner-nav-btn { padding: 10px 4px; background: none; border: none; border-bottom: 2.5px solid transparent; font-size: 0.9rem; font-weight: 700; color: #8888aa; cursor: pointer; transition: all 0.2s; }
                .inner-nav-btn.active { color: #f58220; border-bottom-color: #f58220; }
                .inner-nav-btn:hover:not(.active) { color: #4a4a6a; }
            `}</style>

            {isLoading && (
                <div className="loading-overlay">
                    <div className="text-[#f58220] font-bold text-xl">Loading...</div>
                </div>
            )}

            <div className="mb-6 flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-black tracking-tight">Split Configuration</h1>
                    <p className="text-[#8888aa] text-sm mt-1">Manage standard and conditional splits logically.</p>
                </div>
                {activeTab === 'list' && (
                    <button className="btn-primary" onClick={handleCreateNew}>+ New Configuration</button>
                )}
            </div>

            <div className="inner-nav">
                <button className={`inner-nav-btn ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')}>Existing Configurations</button>
                <button className={`inner-nav-btn ${activeTab === 'form' ? 'active' : ''}`} onClick={() => setActiveTab('form')}>Configure Split</button>
            </div>

            {activeTab === 'list' ? (
                <div>
                    {configsList.length === 0 ? (
                        <div className="text-center py-16 border-2 border-dashed border-[#e2e0d8] rounded-xl">
                            <div className="text-4xl mb-3 opacity-40">📂</div>
                            <h3 className="font-bold text-[#4a4a6a] mb-1">No configurations found</h3>
                            <p className="text-[#8888aa] text-sm mb-4">You haven't set up any split rules yet.</p>
                            <button className="btn-primary" onClick={handleCreateNew}>Create First Configuration</button>
                        </div>
                    ) : (
                        configsList.map((c, i) => {
                            const isComplex = c.splitType.toLowerCase() === 'complex';
                            const subCount = isComplex
                                ? c.rules?.reduce((acc, r) => acc + (r.beneficiaries?.length || 0), 0)
                                : c.beneficiaries?.length || 0;
                            const rulesCount = c.rules?.length || 0;

                            return (
                                <div key={i} className="config-list-item">
                                    <div>
                                        <span className={`split-badge ${isComplex ? 'badge-complex' : 'badge-simple'}`}>
                                            {c.splitType}
                                        </span>
                                        <h3 className="font-bold text-[#030229] mb-1">
                                            {isComplex ? `Conditional Rule Split (${rulesCount} rules)` : 'Standard Global Split'}
                                        </h3>
                                        <p className="text-sm text-[#8888aa]">
                                            Contains {subCount} total beneficiaries combined.
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button className="action-btn" onClick={() => handleEdit(c)}>Edit</button>
                                        {/* <button className="action-btn action-btn-danger" onClick={() => handleRemove(c)}>Remove</button> */}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                <div className="animate-fade-in">
                    <div className="text-[0.7rem] font-bold tracking-widest text-[#8888aa] uppercase mb-2">Split Type</div>
                    <div className="inline-flex bg-white border border-[#e2e0d8] rounded-xl p-2 mb-7 shadow-sm">
                        <button className={`type-btn ${splitType === 'simple' ? 'active' : ''}`} onClick={() => setSplitType('simple')}>Simple Split</button>
                        <button className={`type-btn ${splitType === 'complex' ? 'active' : ''}`} onClick={() => { setSplitType('complex'); if (complexRules.length === 0) addComplexRule(); }}>Complex Split</button>
                    </div>
                    <br />

                    {splitType === 'simple' ? (
                        <div className="card">
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-[0.72rem] font-bold mb-1.5 text-[#4a4a6a]">Split Mode</label>
                                    <select value={simpleMode} onChange={e => setSimpleMode(e.target.value)}>
                                        <option value="percent">Percentage (%)</option>
                                        <option value="flat">Flat Amount (NGN)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-[1fr_130px_32px] gap-2 text-[0.66rem] font-bold text-[#b0b0c8] uppercase mb-2">
                                <span>Beneficiary Name</span>
                                <span>Value ({simpleMode === 'percent' ? '%' : 'NGN'})</span>
                            </div>
                            {simpleSubs.map(s => (
                                <div key={s.id} className="grid grid-cols-[1fr_130px_32px] gap-2 mb-2">
                                    <input type="text" placeholder="e.g. Vendor Account" value={s.name} onChange={e => updateSimpleSub(s.id, 'name', e.target.value)} />
                                    <div className="relative">
                                        <input type="number" placeholder="0" value={s.value} onChange={e => updateSimpleSub(s.id, 'value', e.target.value)} />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[0.7rem] text-[#8888aa]">{simpleMode === 'percent' ? '%' : 'NGN'}</span>
                                    </div>
                                    <button className="flex items-center justify-center border border-[#e2e0d8] bg-white rounded-md text-[#b0b0c8] hover:text-[#bb4430] hover:border-[#bb4430] transition-colors" onClick={() => removeSimpleSub(s.id)}>✕</button>
                                </div>
                            ))}
                            <div className="merchant-row mt-2">
                                <div className="flex items-center gap-2">
                                    <span className="bg-[#f58220] text-white text-[0.62rem] font-bold px-2 py-0.5 rounded-full uppercase">YOU</span>
                                    <span className="font-semibold text-sm">Merchant (You)</span>
                                </div>
                                <div className="bg-white border border-[rgba(245,130,32,0.2)] rounded-md py-2 px-3 text-right font-black text-[#f58220]">
                                    {simpleIsOver ? '0' : (simpleRemainder !== null ? simpleRemainder.toFixed(1) : 'Rem')} {simpleMode === 'percent' ? '%' : 'NGN'}
                                </div>
                                <div className="flex justify-center opacity-30 text-[#f58220]">🔒</div>
                            </div>
                            <button className="mt-4 border-2 border-dashed border-[#e2e0d8] bg-white text-[#8888aa] text-sm font-semibold py-2 px-4 rounded-lg hover:border-[#f58220] hover:text-[#f58220] transition-colors" onClick={addSimpleSub}>+ Add Beneficiary</button>
                            <AllocationBar subsArr={simpleSubs} beneTotal={simpleBeneTotal} remainder={simpleRemainder} isOver={simpleIsOver} mode={simpleMode} />
                        </div>
                    ) : (
                        <div>
                            {complexRules.map((rule, idx) => {
                                const beneTotal = rule.subs.reduce((s, x) => s + (parseFloat(x.value) || 0), 0);
                                const remainder = rule.mode === 'percent' ? Math.max(0, 100 - beneTotal) : null;
                                const isOver = rule.mode === 'percent' && beneTotal > 100;
                                return (
                                    <div key={rule.id} className="card relative">
                                        <div className="flex items-center gap-3 mb-5">
                                            <div className="w-9 h-9 bg-[#fff4e6] rounded-lg flex items-center justify-center text-lg">🔑</div>
                                            <div>
                                                <h3 className="font-black text-lg">Split Rule {idx + 1}</h3>
                                                <div className="text-[0.7rem] text-[#8888aa]">Triggered by description keyword</div>
                                            </div>
                                            {complexRules.length > 1 && <button className="ml-auto text-xs font-bold text-[#8888aa] border border-[#e2e0d8] bg-white px-2 py-1 rounded hover:bg-[#fff5f5] hover:border-[#fff5f5] hover:text-[#bb4430] transition-colors" onClick={() => removeComplexRule(rule.id)}>Remove Rule</button>}
                                        </div>
                                        <br />

                                        <div className="grid grid-cols-2 gap-4 mb-5">
                                            <div>
                                                <label className="block text-[0.72rem] font-bold mb-1.5 text-[#4a4a6a]">Description Keyword</label>
                                                <input type="text" placeholder="e.g. Marriage" value={rule.keyword} onChange={e => updateComplexRuleField(rule.id, 'keyword', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-[0.72rem] font-bold mb-1.5 text-[#4a4a6a]">Mode</label>
                                                <select value={rule.mode} onChange={e => updateComplexRuleField(rule.id, 'mode', e.target.value)}>
                                                    <option value="percent">Percentage (%)</option>
                                                    <option value="flat">Flat Amount (NGN)</option>
                                                </select>
                                            </div>
                                        </div>
                                        <br />

                                        {rule.subs.map(s => (
                                            <div key={s.id} className="grid grid-cols-[1fr_130px_32px] gap-2 mb-2">
                                                <input type="text" placeholder="Beneficiary Account" value={s.name} onChange={e => updateComplexRuleSub(rule.id, s.id, 'name', e.target.value)} />
                                                <div className="relative">
                                                    <input type="number" value={s.value} onChange={e => updateComplexRuleSub(rule.id, s.id, 'value', e.target.value)} />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[0.7rem] text-[#8888aa]">{rule.mode === 'percent' ? '%' : 'NGN'}</span>
                                                </div>
                                                <button className="flex items-center justify-center border border-[#e2e0d8] bg-white rounded-md text-[#b0b0c8] hover:text-[#bb4430] hover:border-[#bb4430] transition-colors" onClick={() => removeComplexRuleSub(rule.id, s.id)}>✕</button>
                                            </div>
                                        ))}
                                        <div className="merchant-row mt-2">
                                            <div className="flex items-center gap-2"><span className="bg-[#f58220] text-white text-[0.62rem] p-1 rounded font-bold">YOU</span><span className="font-bold text-sm">Merchant</span></div>
                                            <div className="bg-white border border-[#f58220]/20 rounded p-2 text-right font-black text-[#f58220]">{isOver ? '0' : (remainder !== null ? remainder.toFixed(1) : 'Rem')} {rule.mode === 'percent' ? '%' : 'NGN'}</div>
                                            <div className="text-center opacity-30">🔒</div>
                                        </div>
                                        <button className="mt-4 text-xs font-bold text-[#8888aa] bg-white border border-dashed border-[#e2e0d8] py-2 px-3 rounded-lg hover:border-[#f58220] hover:text-[#f58220] transition-colors" onClick={() => addComplexRuleSub(rule.id)}>+ Add Beneficiary</button>
                                        <AllocationBar subsArr={rule.subs} beneTotal={beneTotal} remainder={remainder} isOver={isOver} mode={rule.mode} />
                                    </div>
                                );
                            })}
                            <button className="w-full border-2 border-dashed border-[#e2e0d8] p-4 rounded-xl text-[#8888aa] font-black hover:border-[#0ca678] hover:text-[#0ca678] transition-all mb-5" onClick={addComplexRule}>+ Add Exclusive Split Rule</button>
                        </div>
                    )}

                    <div className="flex justify-between items-center mt-8 pt-5 border-t border-[#e2e0d8]">
                        <button className="text-[#8888aa] hover:text-[#4a4a6a] font-bold text-sm transition-colors py-2" onClick={() => setActiveTab('list')}>
                            ← Back to List
                        </button>
                        <div className="flex gap-3">
                            <button className="bg-white border border-[#e2e0d8] px-6 py-2.5 rounded-lg text-sm font-bold text-[#4a4a6a]" onClick={() => window.location.reload()}>Reset Defaults</button>
                            <button disabled={saveStatus === 'Saving...'} className={`btn-primary ${saveStatus === '✓ Saved!' ? 'bg-[#0ca678]' : ''}`} onClick={saveConfig}>{saveStatus}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SplitConfig;
